// ============================================================================
// AUTH PROVIDER - ADMIN APP
// ============================================================================

import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import {
  supabase,
  getUserProfile,
  signOut as supabaseSignOut,
  signIn,
} from '@/lib/supabase-client';
import { useNavigate, useLocation } from 'react-router-dom';

interface UserProfile {
  id: string;
  email: string;
  accountId: string;
  role: string;
  firstName?: string;
  lastName?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>(undefined!);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const pathname = useLocation().pathname;
  const fetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const manualSignOutRef = useRef(false);

  const fetchProfile = async (userId: string, user?: User) => {
    try {
      const { data, error } = await getUserProfile(userId);

      if (error) {
        // Profile not found in database - log error but don't try to create
        // Profiles must be created via migrations or explicit admin actions
        if (error.code === 'PGRST116' && user) {
          console.error('Profile not found in database for user:', user.email);
          console.error('Users must be created via migrations or seed scripts, not automatically');
          // Don't set a fallback profile - let the app handle missing profile
          setProfile(null);
          return;
        }
        console.error('Error fetching profile:', error);
        return;
      }

      // Map database fields (snake_case) to interface fields (camelCase)
      const profile: UserProfile = {
        id: data.id,
        email: data.email,
        accountId: data.account_id,
        role: data.role,
        firstName: data.first_name,
        lastName: data.last_name,
        isActive: data.is_active ?? true,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };

      setProfile(profile);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const forcedLogoutRef = useRef(false);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (event === 'SIGNED_OUT') {
        const wasManual = manualSignOutRef.current;
        manualSignOutRef.current = false;
        setProfile(null);
        setLoading(false);

        if (!wasManual && !forcedLogoutRef.current) {
          forcedLogoutRef.current = true;
          navigate('/login?reason=forced_logout', { replace: true });
        }
        return;
      }

      if (fetchTimerRef.current) {
        clearTimeout(fetchTimerRef.current);
      }

      if (session?.user) {
        fetchTimerRef.current = setTimeout(async () => {
          await fetchProfile(session.user.id, session.user);
          fetchTimerRef.current = null;
          setLoading(false);
        }, 100);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => {
      subscription.unsubscribe();
      if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
    };
  }, []);

  const handleSignIn = async (email: string, password: string) => {
    const { data, error } = await signIn(email, password);

    if (error) {
      throw error;
    }

    navigate('/dashboard');
  };

  const handleSignOut = async () => {
    manualSignOutRef.current = true;
    await supabaseSignOut();
    navigate('/login');
  };

  const refreshProfile = async () => {
    if (user?.id) {
      await fetchProfile(user.id);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        session,
        loading,
        signIn: handleSignIn,
        signOut: handleSignOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const ROLE_HIERARCHY: Record<string, number> = {
  SUPER_ADMIN: 5,
  ADMIN: 4,
  MERCHANT: 3,
  STAFF: 2,
  SHOPPER: 1,
};

// Hook to check if user has specific role
export function useHasRole(requiredRole: string): boolean {
  const { profile, loading } = useAuth();

  if (loading) return false;

  const userRoleLevel = ROLE_HIERARCHY[profile?.role ?? ''] ?? 0;
  const requiredRoleLevel = ROLE_HIERARCHY[requiredRole] ?? 0;

  return userRoleLevel >= requiredRoleLevel;
}

// Hook to check if user can access account
export function useCanAccessAccount(accountId: string): boolean {
  const { profile, loading } = useAuth();

  if (loading) return false;
  if (!profile) return false;

  // Admins can access any account
  if (profile.role === 'ADMIN' || profile.role === 'SUPER_ADMIN') return true;

  // Others can only access their own account
  return profile.accountId === accountId;
}
